import React, { useEffect, useRef } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    RadialLinearScale
} from 'chart.js';
import { Bar, Line, Pie, Radar, Scatter, Doughnut } from 'react-chartjs-2';
import logoSvg from '../styles/Geohublogo.svg';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    RadialLinearScale
);

const ChartRenderer = ({ chartConfig }) => {
    const chartRef = useRef(null);
    const containerRef = useRef(null);
    const logoRef = useRef(null);

    useEffect(() => {
        // Set chart background to white
        if (containerRef.current) {
            const canvas = containerRef.current.querySelector('canvas');
            if (canvas) {
                canvas.style.backgroundColor = '#ffffff';
            }
        }
    }, []);

    const handleDownload = (format) => {
        if (!containerRef.current) return;
        const canvas = containerRef.current.querySelector('canvas');
        if (!canvas || !logoRef.current) return;

        // Create a new canvas for the final image
        const finalCanvas = document.createElement('canvas');
        const ctx = finalCanvas.getContext('2d');

        // Set the canvas size to match the chart
        finalCanvas.width = canvas.width;
        finalCanvas.height = canvas.height;

        // Draw white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

        // Draw the chart
        ctx.drawImage(canvas, 0, 0);

        // Calculate logo position (bottom right corner)
        const logoSize = 60; // Adjust logo size as needed
        const padding = 10;
        const logoX = finalCanvas.width - logoSize - padding;
        const logoY = finalCanvas.height - logoSize - padding;

        // Draw the logo
        ctx.drawImage(logoRef.current, logoX, logoY, logoSize, logoSize);

        // Get canvas data URL
        const dataUrl = finalCanvas.toDataURL(`image/${format}`);

        // Create download link
        const link = document.createElement('a');
        link.download = `chart-${Date.now()}.${format}`;
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Check if we have a valid chart configuration
    if (!chartConfig || !chartConfig.type || !chartConfig.data) {
        return <div>Invalid chart configuration</div>;
    }

    const renderChart = () => {
        const props = {
            ref: chartRef,
            options: chartConfig.options || {},
            data: chartConfig.data,
            width: "100%",
            height: "100%"
        };

        switch (chartConfig.type.toLowerCase()) {
            case 'line':
                return <Line {...props} />;
            case 'bar':
                return <Bar {...props} />;
            case 'pie':
                return <Pie {...props} />;
            case 'doughnut':
                return <Doughnut {...props} />;
            case 'radar':
                return <Radar {...props} />;
            case 'scatter':
                return <Scatter {...props} />;
            default:
                return <Bar {...props} />;
        }
    };

    return (
        <div style={{ width: '100%', maxWidth: '800px', margin: '0 auto' }}>
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '300px',
                    position: 'relative',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    padding: '20px'
                }}
                ref={containerRef}
            >
                {renderChart()}
                <img
                    ref={logoRef}
                    src={logoSvg}
                    alt="GeoHub Logo"
                    className="chart-logo"
                    style={{ display: 'none' }}
                    onLoad={(e) => {
                        // Keep a hidden copy of the logo for canvas operations
                        e.target.style.display = 'none';
                    }}
                />
                {/* Visible logo for display */}
                <img
                    src={logoSvg}
                    alt="GeoHub Logo"
                    className="chart-logo"
                    style={{
                        position: 'absolute',
                        bottom: '20px',
                        right: '20px',
                        width: '60px',
                        height: '60px',
                        pointerEvents: 'none'
                    }}
                />
            </div>
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
                marginTop: '15px'
            }}>
                <button
                    onClick={() => handleDownload('png')}
                    style={{
                        backgroundColor: '#2241dd',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '15px',
                        fontWeight: '500',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 4px rgba(34, 65, 221, 0.2)'
                    }}
                    onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#1a33b1';
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 8px rgba(34, 65, 221, 0.3)';
                    }}
                    onMouseOut={(e) => {
                        e.target.style.backgroundColor = '#2241dd';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 4px rgba(34, 65, 221, 0.2)';
                    }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Download Chart
                </button>
            </div>
        </div>
    );
};

export default ChartRenderer; 